import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FiEye,
  FiTrash2,
  FiAlertTriangle,
  FiDownload,
  FiRefreshCw,
  FiGrid,
  FiCheckSquare,
  FiX
} from 'react-icons/fi';

const AuditDashboard = ({ onClose }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    action_type: 'deletions',
    user_id: '',
    start_date: '',
    end_date: '',
    limit: 50
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`/api/planwise/audit-logs?${params}`);
      setAuditLogs(response.data.logs);
      setStats(response.data.stats);
      setError(null);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get('/api/planwise/team-members');
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    fetchAuditLogs();
  }, [filters]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDetails = (details) => {
    if (!details) return 'No details available';
    if (typeof details === 'string') return details;

    if (typeof details === 'object') {
      const { task_title, board_name, board_title, comment_preview } = details;

      const parts = [];
      if (task_title) parts.push(`Task: ${task_title}`);
      if (board_name) parts.push(`Board: ${board_name}`);
      if (board_title) parts.push(`Board: ${board_title}`);
      if (comment_preview) parts.push(`Comment: ${comment_preview}`);

      if (parts.length > 0) {
        return parts.join(' | ');
      }

      return JSON.stringify(details);
    }

    return String(details);
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'board_deleted':
        return <FiAlertTriangle className="w-3.5 h-3.5 text-red-600 dark:text-red-500" />;
      case 'task_deleted':
        return <FiTrash2 className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />;
      default:
        return <FiEye className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />;
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'board_deleted':
        return 'Board Deleted';
      case 'task_deleted':
        return 'Task Deleted';
      default:
        return actionType;
    }
  };

  const clearFilters = () => {
    setFilters({
      action_type: '',
      user_id: '',
      start_date: '',
      end_date: '',
      limit: 50
    });
  };

  const exportToCSV = () => {
    if (auditLogs.length === 0) return;
    const csvData = auditLogs.map(log => ({
      Date: formatDate(log.created_at),
      Action: getActionLabel(log.action_type),
      User: log.user ? `${log.user.firstName} ${log.user.lastName}` : 'Unknown User',
      Email: log.user ? log.user.email : 'N/A',
      Item: log.action_type === 'board_deleted'
        ? (log.details?.board_title || log.details?.board_name || 'Deleted Board')
        : (log.details?.task_title || 'Deleted Task'),
      Details: formatDetails(log.details),
      Board: log.board?.name || log.details?.board_title || 'N/A',
      Impact: log.action_type === 'board_deleted'
        ? `${log.details?.summary?.total_tasks || 0} tasks, ${log.details?.summary?.total_collaborators || 0} collaborators`
        : 'Single task'
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `planwise-audit-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const inputCls = "w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white";
  const labelCls = "block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1";

  return (
    <div
      className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onWheel={(e) => e.stopPropagation()}
      onScroll={(e) => e.stopPropagation()}
    >
      <div
        className="w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FiEye className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Audit Dashboard</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Track deletions and accountability</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={clearFilters}
              className="px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              Clear Filters
            </button>
            <button
              onClick={exportToCSV}
              disabled={auditLogs.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md text-xs font-medium transition-colors"
            >
              <FiDownload className="w-3.5 h-3.5" />
              Export CSV
            </button>
            <button
              onClick={fetchAuditLogs}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-xs font-medium transition-colors"
            >
              <FiRefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2.5 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-medium text-red-700 dark:text-red-400 uppercase tracking-wide">Total Deletions</p>
                    <p className="text-xl font-bold text-red-800 dark:text-red-300">{stats.totalDeletions}</p>
                  </div>
                  <FiTrash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
              </div>

              <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-medium text-blue-700 dark:text-blue-400 uppercase tracking-wide">Board Deletions</p>
                    <p className="text-xl font-bold text-blue-800 dark:text-blue-300">{stats.boardDeletions}</p>
                  </div>
                  <FiGrid className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>

              <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide">Task Deletions</p>
                    <p className="text-xl font-bold text-amber-800 dark:text-amber-300">{stats.taskDeletions}</p>
                  </div>
                  <FiCheckSquare className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/20">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div>
              <label className={labelCls}>Action Type</label>
              <select
                value={filters.action_type}
                onChange={(e) => setFilters({ ...filters, action_type: e.target.value })}
                className={inputCls}
              >
                <option value="">All Actions</option>
                <option value="deletions">All Deletions</option>
                <option value="board_deleted">Board Deletions</option>
                <option value="task_deleted">Task Deletions</option>
              </select>
            </div>

            <div>
              <label className={labelCls}>User</label>
              <select
                value={filters.user_id}
                onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                className={inputCls}
              >
                <option value="">All Users</option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.firstName} {member.lastName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls}>Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex flex-col items-center justify-center flex-1 py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mb-2"></div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Loading audit logs…</p>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 py-10 text-gray-500 dark:text-gray-400">
              <FiEye className="w-10 h-10 text-gray-400 mb-2" />
              <p className="text-sm font-medium mb-1">No audit logs found</p>
              <p className="text-xs">Try adjusting your filters or check back later</p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/30 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Action</th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Details</th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action_type)}
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {getActionLabel(log.action_type)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-700 dark:text-gray-300">
                          {log.user ? `${log.user.firstName} ${log.user.lastName}` : 'Unknown User'}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-xs text-gray-700 dark:text-gray-300 max-w-xs truncate">
                          {formatDetails(log.details)}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-700 dark:text-gray-300">
                          {formatDate(log.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="px-2.5 py-1 text-[10px] font-medium bg-primary-600 hover:bg-primary-700 text-white rounded transition-colors"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="w-full max-w-xl bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <FiEye className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Audit Log Details</h3>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-0.5">Action Type</label>
                <p className="text-sm text-gray-900 dark:text-white">{getActionLabel(selectedLog.action_type)}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-0.5">User</label>
                <p className="text-sm text-gray-900 dark:text-white">
                  {selectedLog.user ? `${selectedLog.user.firstName} ${selectedLog.user.lastName}` : 'Unknown User'}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-0.5">Date & Time</label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedLog.created_at)}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-0.5">Details</label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDetails(selectedLog.details)}</p>
              </div>

              {selectedLog.metadata && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-0.5">Metadata</label>
                  <pre className="text-[11px] text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 p-2.5 rounded-md overflow-x-auto border border-gray-200 dark:border-gray-700">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-3 py-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditDashboard;
